import * as React from "react"
import { useTranslation } from "react-i18next"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"
import { modalOverlayClassName } from "../../lib/overlay"
import {
 isFlatpickrOutsideEvent,
 isReactSelectOutsideEvent,
} from "../../lib/react-select-portal"

function isDialogDescriptionElement(el: React.ReactElement): boolean {
 const type = el.type as { displayName?: string }
 return (
 el.type === DialogPrimitive.Description
 || type?.displayName === DialogPrimitive.Description.displayName
 )
}

function isDialogTitleElement(el: React.ReactElement): boolean {
 const type = el.type as { displayName?: string }
 return (
 el.type === DialogPrimitive.Title
 || type?.displayName === DialogPrimitive.Title.displayName
 )
}

function containsDialogChild(
 node: React.ReactNode,
 predicate: (el: React.ReactElement) => boolean
): boolean {
 return React.Children.toArray(node).some((child) => {
 if (!React.isValidElement(child)) return false
 if (predicate(child)) return true
 if (child.type === React.Fragment) {
 return containsDialogChild((child.props as { children?: React.ReactNode }).children, predicate)
 }
 const props = child.props as { children?: React.ReactNode }
 if (props?.children != null) return containsDialogChild(props.children, predicate)
 return false
 })
}

/** Radix warns if there is no `DialogTitle`/`DialogDescription`; recurse so we do not add duplicates. */
function containsDialogTitle(node: React.ReactNode): boolean {
 return containsDialogChild(node, isDialogTitleElement)
}

function containsDialogDescription(node: React.ReactNode): boolean {
 return containsDialogChild(node, isDialogDescriptionElement)
}

const Dialog = ({
 modal = true,
 ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & { modal?: boolean }) => (
 <DialogPrimitive.Root modal={modal} {...props} />
)
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Overlay>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Overlay
 ref={ref}
 className={cn(modalOverlayClassName, "z-50", className)}
 {...props}
 />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Content>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onPointerDownOutside, onInteractOutside, onFocusOutside, ...props }, ref) => {
 const { t } = useTranslation()
 const showFallbackTitle = !containsDialogTitle(children)
 const showFallbackDescription =
 !Object.prototype.hasOwnProperty.call(props,"aria-describedby")
 && !containsDialogDescription(children)

 return (
 <DialogPortal>
 <DialogOverlay className="z-[200]" />
 <DialogPrimitive.Content
 ref={ref}
 className={cn(
"fixed left-[50%] top-[50%] z-[201] flex w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 border border-border-subtle bg-popover p-6 text-popover-foreground shadow-modal duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-xl max-h-[calc(100dvh-2rem)] overflow-y-auto overflow-x-hidden dark:border-border-subtle",
 className
 )}
 onPointerDownOutside={(e) => {
 if (isReactSelectOutsideEvent(e) || isFlatpickrOutsideEvent(e)) {
 e.preventDefault()
 }
 onPointerDownOutside?.(e)
 }}
 onInteractOutside={(e) => {
 if (isReactSelectOutsideEvent(e) || isFlatpickrOutsideEvent(e)) {
 e.preventDefault()
 }
 onInteractOutside?.(e)
 }}
 onFocusOutside={(e) => {
 if (isReactSelectOutsideEvent(e) || isFlatpickrOutsideEvent(e)) {
 e.preventDefault()
 }
 onFocusOutside?.(e)
 }}
 {...props}
 >
 {showFallbackTitle && (
 <DialogPrimitive.Title className="sr-only">
 {t('ui.dialog.fallbackTitle')}
 </DialogPrimitive.Title>
 )}
 {showFallbackDescription && (
 <DialogPrimitive.Description className="sr-only">
 {t('ui.dialog.fallbackDescription')}
 </DialogPrimitive.Description>
 )}
 {children}
 <DialogPrimitive.Close
 aria-label={t('ui.close')}
 className="absolute end-4 top-4 flex h-8 w-8 items-center justify-center rounded-lg border border-destructive/25 bg-destructive/10 text-destructive shadow-sm ring-offset-background transition-colors hover:border-destructive/40 hover:bg-destructive/15 focus:outline-none focus:ring-2 focus:ring-destructive/30 focus:ring-offset-2 focus-visible:ring-offset-2 disabled:pointer-events-none dark:bg-destructive/15 dark:hover:bg-destructive/20 dark:ring-offset-background"
 >
 <X className="h-4 w-4" aria-hidden />
 <span className="sr-only">{t('ui.close')}</span>
 </DialogPrimitive.Close>
 </DialogPrimitive.Content>
 </DialogPortal>
 )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
 className,
 ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
 <div
 className={cn(
"flex min-w-0 flex-col space-y-1.5 pe-12 text-center sm:text-start",
 className
 )}
 {...props}
 />
)
DialogHeader.displayName ="DialogHeader"

const DialogFooter = ({
 className,
 ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
 <div
 className={cn(
"flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 sm:space-x-reverse gap-2",
 className
 )}
 {...props}
 />
)
DialogFooter.displayName ="DialogFooter"

const DialogTitle = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Title>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Title
 ref={ref}
 className={cn(
"break-words text-lg font-semibold leading-tight tracking-tight",
 className
 )}
 {...props}
 />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Description>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Description
 ref={ref}
 className={cn("text-sm text-muted-foreground", className)}
 {...props}
 />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
 Dialog,
 DialogPortal,
 DialogOverlay,
 DialogClose,
 DialogTrigger,
 DialogContent,
 DialogHeader,
 DialogFooter,
 DialogTitle,
 DialogDescription,
}
